import { useTranslation } from "react-i18next";
/* ----------------- Globals --------------- */
import { Pressable } from "react-native";
import { Text } from "react-native-paper";

/* ----------------- Types --------------- */
import type { HeaderSectionData, TaskCardProps } from "@features/dashboard/";

/* ----------------- Hooks --------------- */
import { useAppTheme } from "@hooks/useAppTheme";

/* ----------------- UI --------------- */
import {
	Box,
	DonutChart,
	DonutChartSkeleton,
	StatsSkeleton,
	useDevice,
} from "@memorang/ui";

/* ----------------- Components --------------- */
import ProductIcon from "components/ProductIcon";

/* ----------------- Icons --------------- */
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import type { PracticeTestExam } from "@memorang/types";
import DiagnosticItemsContainer from "containers/DiagnosticItemsContainer";
import type { DiagnosticCardProps } from "types/tag";
import { ModuleRecommendationContent } from "./ModuleRecommendationsSection";

interface Props {
	headerData?: HeaderSectionData;
	exams?: PracticeTestExam[];
	hasAccess?: boolean;
	handleShowPurchase: () => void;
	handleQuickStudy?: () => void;
	quickStudyRecommendation?: TaskCardProps;
	recommendationItems?: DiagnosticCardProps[];
}

const getQuickStudyDescription = (
	status?: TaskCardProps["status"],
	defaultDescription?: string,
) => {
	if (status === "ACTIVE") {
		return "Pick up where you left off in your current module.";
	}
	if (status === "NOT_STARTED") {
		return "Start the next module to keep your momentum going.";
	}
	if (status === "COMPLETE") {
		return "Review your most recent module results and key takeaways.";
	}
	return (
		defaultDescription || "An adaptive session targets your weakest areas."
	);
};

const ExamInfoSection = ({
	headerData,
	exams,
	hasAccess,
	handleShowPurchase,
	handleQuickStudy,
	quickStudyRecommendation,
	recommendationItems,
}: Props) => {
	const theme = useAppTheme();
	const { isSmallScreen } = useDevice();
	const { t } = useTranslation();
	const quickStudyDescription = getQuickStudyDescription(
		quickStudyRecommendation?.status,
		t("dashboard.quickStudy.subtitle"),
	);
	const fallbackRecommendation: TaskCardProps = {
		title: t("dashboard.quickStudy.title"),
		subtitle: quickStudyDescription,
		status: "NOT_STARTED",
		handleClickTask: handleQuickStudy || (() => undefined),
		body: "",
	};
	const quickStudyItem = quickStudyRecommendation
		? {
				...quickStudyRecommendation,
				subtitle: quickStudyRecommendation.subtitle || quickStudyDescription,
			}
		: fallbackRecommendation;
	const statSurfaceColor = theme.colors.surface;
	const sectionBorderColor = theme.colors.outlineVariant || "#eae7de";

	return (
		<Box gap={16}>
			<Box
				style={{
					backgroundColor: theme.colors.surface,
					borderWidth: 1,
					borderColor: sectionBorderColor,
					borderRadius: theme.roundness,
					overflow: "hidden",
				}}
			>
				<Box
					style={{
						backgroundColor: theme.colors.surface,
						padding: 16,
						flexDirection: isSmallScreen ? "column" : "row",
						justifyContent: "space-between",
						alignItems: isSmallScreen ? "stretch" : "center",
						gap: isSmallScreen ? 20 : 24,
					}}
				>
					<Box flexDirection="row" alignItems="center" gap={16} flex={1}>
						{headerData ? (
							<DonutChart
								distribution={headerData.distribution}
								radius={45}
								innerRadius={36}
							>
								{headerData.examIcon ? (
									<ProductIcon
										image={headerData.examIcon}
										size={42}
										showBorder={false}
									/>
								) : (
									<Icon
										name="file-document-outline"
										size={48}
										color={theme.colors.onSurfaceVariant}
									/>
								)}
							</DonutChart>
						) : (
							<DonutChartSkeleton size={90} isSmallScreen={isSmallScreen} />
						)}
						<Box flex={1} gap={4}>
							<Text
								variant="titleLarge"
								style={{
									fontWeight: "bold",
									color: theme.colors.onSurface,
									fontSize: 22,
									lineHeight: 28,
								}}
							>
								{headerData?.examName || "TOEIC Link"}
							</Text>
							<Text
								variant="bodyMedium"
								style={{
									color: theme.colors.onSurfaceVariant,
									fontSize: 14,
									lineHeight: 20,
								}}
							>
								{headerData?.examStatusText || "Let's keep the momentum going."}
							</Text>
						</Box>
					</Box>

					{headerData ? (
						<Box
							flexDirection="row"
							gap={8}
							justifyContent="space-between"
							alignItems="stretch"
							width={isSmallScreen ? "100%" : undefined}
							flex={isSmallScreen ? undefined : 1}
						>
							{headerData.headerStats.map((stat) => (
								<Pressable
									key={`${stat.label}-${stat.value}`}
									onPress={stat.onClick}
									style={{
										flex: 1,
										alignItems: "center",
										borderWidth: 1,
										borderColor: sectionBorderColor,
										borderRadius: 0,
										paddingVertical: 12,
										paddingHorizontal: 8,
										gap: 4,
										minWidth: 0,
										cursor: stat.onClick ? "pointer" : "auto",
										backgroundColor: statSurfaceColor,
									}}
								>
									<Icon
										// biome-ignore lint/suspicious/noExplicitAny: icon name can be generic string
										name={stat.icon as any}
										color={stat.color || theme.colors.onSurfaceVariant}
										size={24}
									/>
									<Text
										variant="titleSmall"
										style={{
											textAlign: "center",
											fontWeight: "bold",
											color: theme.colors.onSurface,
											fontSize: 14,
										}}
									>
										{stat.value}
									</Text>
									<Text
										variant="labelSmall"
										style={{
											color: theme.colors.onSurfaceVariant,
											textAlign: "center",
											fontSize: 10,
										}}
									>
										{stat.label}
									</Text>
								</Pressable>
							))}
						</Box>
					) : (
						<StatsSkeleton isSmallScreen={isSmallScreen} />
					)}
				</Box>

				{handleQuickStudy && (
					<>
						<Box
							style={{
								height: 1,
								backgroundColor: sectionBorderColor,
							}}
						/>
						<Box
							style={{
								backgroundColor: theme.colors.surface,
								padding: 16,
								flexDirection: isSmallScreen ? "column" : "row",
								justifyContent: "space-between",
								alignItems: isSmallScreen ? "stretch" : "center",
								gap: 16,
							}}
						>
							<ModuleRecommendationContent
								item={quickStudyItem}
								variant="inline"
							/>
						</Box>
					</>
				)}
			</Box>
			<DiagnosticItemsContainer
				exams={exams}
				recommendationItems={recommendationItems}
				hasAccess={hasAccess}
				isDataLoading={!headerData}
				handleShowPurchase={handleShowPurchase}
			/>
		</Box>
	);
};

export default ExamInfoSection;
